import { useCallback, useEffect, useState } from "react";
import "./viewportTable.css";

export const fittingTablePageSize = ({ height, rowHeight = 52, headerHeight = 52, footerHeight = 52 }) =>
  Math.min(100, Math.max(8, Math.ceil((height - headerHeight - footerHeight) / Math.max(16, rowHeight))));

export const initialTablePageSize = () => fittingTablePageSize({
  height: Math.max(160, window.innerHeight - 280),
});

// Measure the real table position and row density, not a fixed device breakpoint.
// Leave room for the existing fixed POS bottom bar. A partial last row can scroll;
// pagination stays visible and we never enlarge rows to fill a sparse last page.
export default function useViewportTablePageSize(setPageState, enabled = true) {
  const [node, setNode] = useState(null);
  const tableRef = useCallback((element) => setNode(element), []);
  useEffect(() => {
    if (!enabled || !node) return undefined;
    let timer;
    const measure = () => {
      const viewport = window.visualViewport?.height || window.innerHeight;
      const height = Math.max(160, Math.floor(viewport - node.getBoundingClientRect().top - 80));
      const measuredHeight = (selector, fallback) => node.querySelector(selector)?.getBoundingClientRect().height || fallback;
      const rows = [...node.querySelectorAll(".cpt__row")];
      const rowHeight = Math.max(52, ...rows.map((row) => row.getBoundingClientRect().height));
      const pageSize = fittingTablePageSize({
        height, rowHeight,
        headerHeight: measuredHeight(".cpt__header", 52),
        footerHeight: measuredHeight(".cpt__pagination", 52),
      });
      node.style.setProperty("--orders-table-height", `${height}px`);
      setPageState((old) => old.pageSize === pageSize ? old : { ...old, pageSize, page: 1 });
    };
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(measure, 120);
    };
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    observer?.observe(node);
    if (node.parentElement) observer?.observe(node.parentElement);
    const mutations = typeof MutationObserver !== "undefined" ? new MutationObserver(schedule) : null;
    mutations?.observe(node, { childList: true, subtree: true, characterData: true });
    window.addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("resize", schedule);
    return () => {
      clearTimeout(timer);
      observer?.disconnect();
      mutations?.disconnect();
      window.removeEventListener("resize", schedule);
      window.visualViewport?.removeEventListener("resize", schedule);
    };
  }, [node, enabled, setPageState]);
  return tableRef;
}
